import { sectionRenderer } from '@theme/section-renderer';
import { CollectionUpdateEvent, SearchUpdateEvent } from '@shopify/events';

/**
 * Collection Filter Module
 * Handles the collection hierarchy tree with checkbox-based filtering.
 *
 * Behavior:
 * - Single selection: AJAX navigate to the collection page (seamless)
 * - Multi-selection: AJAX navigate to search page with filter params (seamless)
 * - Clear all: Reset to show all products from the current page
 */

/**
 * Initialize collection filter functionality on a container element
 * @param {HTMLElement} container - The collection filter container element
 */
function initCollectionFilter(container) {
  if (!container || container.dataset.initialized === 'true') return;
  container.dataset.initialized = 'true';

  const sectionId = container.dataset.sectionId || '';

  // Handle expand/collapse toggle clicks (event delegation)
  container.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-toggle]');
    if (toggle) {
      handleToggle(toggle);
    }
  });

  // Handle checkbox changes (event delegation)
  container.addEventListener('change', (event) => {
    const checkbox = event.target.closest('.collection-filter__checkbox');
    if (checkbox) {
      handleCheckboxChange(checkbox, container, sectionId);
    }
  });

  // Handle clear all button
  const clearButton = container.querySelector('[data-clear-all]');
  if (clearButton) {
    clearButton.addEventListener('click', () => {
      clearAll(container, sectionId);
    });
    updateClearButtonState(container);
  }

  // Sync checkbox state with URL parameters on init
  syncStateFromURL(container);
}

/**
 * Handle expand/collapse toggle for tree nodes
 * @param {HTMLElement} toggle - The toggle button element
 */
function handleToggle(toggle) {
  const parentNode = toggle.closest('.collection-filter__node');
  if (!parentNode) return;

  const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!isExpanded));

  if (isExpanded) {
    parentNode.classList.add('collection-filter__node--collapsed');
  } else {
    parentNode.classList.remove('collection-filter__node--collapsed');
  }
}

/**
 * Handle checkbox change events
 * @param {HTMLInputElement} checkbox - The changed checkbox
 * @param {HTMLElement} container - The container element
 * @param {string} sectionId - The section ID
 */
async function handleCheckboxChange(checkbox, container, sectionId) {
  // Update clear button state
  updateClearButtonState(container);

  // Get all currently checked checkboxes
  const checkedBoxes = getCheckedCheckboxes(container);

  if (checkedBoxes.length === 0) {
    // All unchecked - reload current collection
    await resetToCurrentCollection(sectionId);
  } else if (checkedBoxes.length === 1) {
    // Single selection - navigate to that collection via AJAX
    const selectedHandle = checkedBoxes[0].value;
    await filterByCollection(selectedHandle, sectionId);
  } else {
    // Multiple selections - navigate to search with filter params via AJAX
    const handles = checkedBoxes.map((cb) => cb.value);
    await filterByMultipleCollections(handles, sectionId);
  }

  // Update URL to reflect current filter state
  updateFilterURL(checkedBoxes);
}

/**
 * Get all checked checkboxes in the container
 * @param {HTMLElement} container - The container element
 * @returns {HTMLInputElement[]} Array of checked checkbox elements
 */
function getCheckedCheckboxes(container) {
  return Array.from(
    container.querySelectorAll('.collection-filter__checkbox:checked')
  );
}

/**
 * Update the clear button disabled state
 * @param {HTMLElement} container - The container element
 */
function updateClearButtonState(container) {
  const checkedBoxes = getCheckedCheckboxes(container);
  const clearButton = container.querySelector('[data-clear-all]');
  if (clearButton) {
    clearButton.disabled = checkedBoxes.length === 0;
  }
}

/**
 * Sync checkbox state from URL parameters
 * @param {HTMLElement} container - The container element
 */
function syncStateFromURL(container) {
  const url = new URL(window.location.href);
  const filterParams = url.searchParams.getAll('filter.p.m.collection');

  if (filterParams.length > 0) {
    // Uncheck all first
    const allCheckboxes = container.querySelectorAll('.collection-filter__checkbox');
    allCheckboxes.forEach((cb) => { cb.checked = false; });

    // Check the ones matching URL params
    filterParams.forEach((handle) => {
      const checkbox = container.querySelector(`.collection-filter__checkbox[value="${handle}"]`);
      if (checkbox) {
        checkbox.checked = true;
        // Expand parent nodes
        const parentNode = checkbox.closest('.collection-filter__node');
        if (parentNode) {
          let parentTree = parentNode.parentElement;
          while (parentTree && parentTree.classList.contains('collection-filter__tree--nested')) {
            const parentNodeItem = parentTree.parentElement;
            if (parentNodeItem) {
              const parentToggle = parentNodeItem.querySelector('[data-toggle]');
              if (parentToggle) {
                parentNodeItem.classList.remove('collection-filter__node--collapsed');
                parentToggle.setAttribute('aria-expanded', 'true');
              }
              parentTree = parentNodeItem.parentElement ? parentNodeItem.parentElement.querySelector('.collection-filter__tree--nested') : null;
            } else {
              break;
            }
          }
        }
      }
    });

    updateClearButtonState(container);
  }
}

/**
 * Update the URL with filter parameters
 * @param {HTMLInputElement[]} checkedBoxes - Array of checked checkbox elements
 */
function updateFilterURL(checkedBoxes) {
  const url = new URL(window.location.href);

  // Remove existing filter params
  url.searchParams.delete('filter.p.m.collection');

  // Add current filter params
  checkedBoxes.forEach((cb) => {
    url.searchParams.append('filter.p.m.collection', cb.value);
  });

  // Update URL without reloading
  history.pushState({}, '', url.toString());
}

/**
 * Filter products by a single collection via AJAX navigation
 * @param {string} handle - The collection handle
 * @param {string} sectionId - The section ID
 */
async function filterByCollection(handle, sectionId) {
  try {
    const url = new URL(window.location.href);

    // Build the collection URL
    const collectionUrl = new URL(`/collections/${handle}`, url.origin);

    // Preserve any non-collection URL parameters
    const currentParams = url.searchParams;
    for (const [key, value] of currentParams.entries()) {
      if (!key.startsWith('filter.p.m') && key !== 'section_id' && key !== 'q') {
        collectionUrl.searchParams.set(key, value);
      }
    }

    // Update URL without reloading
    history.pushState({}, '', collectionUrl.toString());

    // Re-render the section
    const renderPromise = sectionRenderer.renderSection(sectionId, {
      url: collectionUrl,
    });

    // Dispatch collection update event to clear pagination cache
    dispatchCollectionUpdateEvent(renderPromise);
  } catch (error) {
    console.error('Error filtering by collection:', error);
    // Fallback to full page navigation
    window.location.href = `/collections/${handle}`;
  }
}

/**
 * Filter products by multiple collections using Section Rendering API
 * Navigates to search page with filter params for seamless product merging
 * @param {string[]} handles - Array of collection handles
 * @param {string} sectionId - The section ID
 */
async function filterByMultipleCollections(handles, sectionId) {
  try {
    // Build search URL with filter params
    const url = new URL(window.location.href);
    const searchUrl = new URL('/search', url.origin);

    // Preserve any existing non-collection, non-search URL parameters
    for (const [key, value] of url.searchParams.entries()) {
      if (!key.startsWith('filter.p.m') && key !== 'section_id' && key !== 'q') {
        searchUrl.searchParams.set(key, value);
      }
    }

    // Add collection filter params
    for (const handle of handles) {
      searchUrl.searchParams.append('filter.p.m.collection', handle);
    }

    // Fetch the search page HTML to find the search-results section ID
    const response = await fetch(searchUrl.toString());
    if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Find the results-list element to get the search-results section ID
    const newResultsList = doc.querySelector('results-list');
    if (!newResultsList) {
      throw new Error('No results-list found in search response');
    }

    const searchSectionId = newResultsList.getAttribute('section-id');
    if (!searchSectionId) {
      throw new Error('No section-id found on results-list');
    }

    // Update browser history with the search URL
    history.pushState({}, '', searchUrl.toString());

    // Re-render using Section Rendering API with the search-results section ID
    const renderPromise = sectionRenderer.renderSection(searchSectionId, {
      url: searchUrl,
    });

    // Dispatch search update event to clear pagination cache
    dispatchSearchUpdateEvent(renderPromise);
  } catch (error) {
    console.error('Error filtering by multiple collections:', error);
    // Fallback to full page navigation
    const url = new URL(window.location.href);
    const searchUrl = new URL('/search', url.origin);
    for (const handle of handles) {
      searchUrl.searchParams.append('filter.p.m.collection', handle);
    }
    window.location.href = searchUrl.toString();
  }
}

/**
 * Dispatch a collection update event to notify other components
 * @param {Promise} renderPromise - The render promise to resolve after completion
 */
function dispatchCollectionUpdateEvent(renderPromise) {
  const resultsList = document.querySelector('results-list');
  if (!resultsList) return;

  const rawId = resultsList.dataset.collectionId;
  const collectionData = {
    id: rawId && /^\d+$/.test(rawId) ? rawId : null,
    handle: resultsList.dataset.collectionHandle || '',
    productsCount: Number(resultsList.dataset.resultsCount) || 0,
  };

  const deferredPromise = CollectionUpdateEvent.createPromise();

  document.dispatchEvent(
    new CollectionUpdateEvent({
      collection: collectionData,
      sortKey: CollectionUpdateEvent.getSortKey(),
      promise: deferredPromise.promise,
    })
  );

  if (renderPromise) {
    renderPromise
      .then(() => deferredPromise.resolve({ productsCount: Number(resultsList.dataset.resultsCount) || 0 }))
      .catch((error) => deferredPromise.reject(error));
  } else {
    deferredPromise.resolve({ productsCount: Number(resultsList.dataset.resultsCount) || 0 });
  }
}

/**
 * Dispatch a search update event to notify other components
 * @param {Promise} renderPromise - The render promise to resolve after completion
 */
function dispatchSearchUpdateEvent(renderPromise) {
  const resultsList = document.querySelector('results-list');
  if (!resultsList) return;

  const deferredPromise = SearchUpdateEvent.createPromise();
  const query = SearchUpdateEvent.parseSearchQuery();
  const productFilters = SearchUpdateEvent.parseProductFilters();

  document.dispatchEvent(
    new SearchUpdateEvent({
      search: {
        query,
        ...(productFilters && { productFilters }),
        sortKey: SearchUpdateEvent.getSortKey(),
      },
      promise: deferredPromise.promise,
    })
  );

  if (renderPromise) {
    renderPromise
      .then(() => deferredPromise.resolve({ totalCount: Number(resultsList.dataset.resultsCount) || 0 }))
      .catch((error) => deferredPromise.reject(error));
  } else {
    deferredPromise.resolve({ totalCount: Number(resultsList.dataset.resultsCount) || 0 });
  }
}

/**
 * Reset to current collection (clear all filters)
 * @param {string} sectionId - The section ID
 */
async function resetToCurrentCollection(sectionId) {
  try {
    const currentUrl = new URL(window.location.href);

    // Get the base URL (remove filter parameters)
    const cleanUrl = new URL(currentUrl.pathname, currentUrl.origin);

    // Preserve non-filter parameters
    for (const [key, value] of currentUrl.searchParams.entries()) {
      if (!key.startsWith('filter.p.m') && key !== 'section_id' && key !== 'q') {
        cleanUrl.searchParams.set(key, value);
      }
    }

    // Update URL without reloading
    history.pushState({}, '', cleanUrl.toString());

    // Re-render the section
    const renderPromise = await sectionRenderer.renderSection(sectionId, {
      url: cleanUrl,
    });

    // Dispatch collection update event
    dispatchCollectionUpdateEvent(Promise.resolve());
  } catch (error) {
    console.error('Error resetting collection:', error);
  }
}

/**
 * Clear all selections and reset
 * @param {HTMLElement} container - The container element
 * @param {string} sectionId - The section ID
 */
async function clearAll(container, sectionId) {
  const checkedBoxes = getCheckedCheckboxes(container);
  checkedBoxes.forEach((cb) => {
    cb.checked = false;
  });

  updateClearButtonState(container);
  await resetToCurrentCollection(sectionId);
}

/**
 * Initialize all collection filter components on the page
 */
function initAll() {
  const containers = document.querySelectorAll('.collection-filter');
  containers.forEach((container) => {
    // Force re-initialization by removing the guard flag
    // This ensures proper re-initialization after Section Rendering API DOM swaps
    delete container.dataset.initialized;
    initCollectionFilter(container);
  });
}

// Wait for DOM to be ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}

// Re-initialize after section re-render (Section Rendering API updates DOM)
document.addEventListener('shopify:section:render', () => {
  initAll();
});

// Handle browser back/forward navigation
window.addEventListener('popstate', () => {
  // Re-sync filter state from URL after navigation
  const containers = document.querySelectorAll('.collection-filter');
  containers.forEach((container) => {
    syncStateFromURL(container);
  });
});